import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ApplicationTaskModel } from '../../../models/borrower-portal/application-task-model';
import { Observer, Subscription, firstValueFrom } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { BorrowerVerificationOrderHistory } from '../../../models/borrower-verification-order-history.model';
import { BorrowerVerificationUserEnrollmentResponse } from '../../../models/borrower-verification-user-enrollment.model';
import { IncomeService } from '../../../services/income-service';
import { PortalServiceFactory } from 'projects/borrower-app/src/app/services/portal-service.factory';
import { PlaidCheckService } from '../../../services/plaid-check.service';
import { NotificationService } from 'projects/shared/services/notification.service';

type VerificationProvider = 'FormFree' | 'Truv' | 'Argyle' | 'PlaidCheck';

@Component({
    selector: 'income-employment-verification-dialog',
    templateUrl: './income-employment-verification-dialog.component.html',
    styleUrls: ['./income-employment-verification-dialog.component.scss'],
    standalone: false
})
export class IncomeEmploymentVerificationDialogComponent implements OnInit, OnDestroy {

  @Input() applicationId: number;
  @Input() task: ApplicationTaskModel;
  @Input() voeEnabled: boolean = false;
  @Input() plaidCheckEnabled: boolean = false;

  protected provider: VerificationProvider | null = null;
  protected verificationProvider: Exclude<VerificationProvider, 'PlaidCheck'> | null = null;
  protected thirdPartyEnrollmentWidget: string = "";
  protected usesOrdersApi: boolean = false;
  protected borrowerVerificationOrderId: number | null = null;

  protected loading: boolean = true;
  protected errorMessage: string = "";
  protected previousAttemptMessage: string = "";

  protected showProviderSelection: boolean = false;
  protected plaidCheckConsentGiven: boolean = false;
  protected plaidCheckLoading: boolean = false;

  private _enrollmentInitialized = false;
  private _enrollmentSubscription?: Subscription;
  private _historySubscription?: Subscription;

  constructor(public activeModal: NgbActiveModal,
    private _spinner: NgxSpinnerService,
    private _incomeService: IncomeService,
    private readonly _portalServiceFactory: PortalServiceFactory,
    private _cdr: ChangeDetectorRef,
    private _plaidCheckService: PlaidCheckService,
    private _notifyService: NotificationService,
    private _elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.updateDialogWidth();

    const observer: Observer<BorrowerVerificationOrderHistory> = {
      next: (order: BorrowerVerificationOrderHistory) => {
        if (order) {
          // Shown alongside the widget rather than instead of it, so the borrower can act on why the
          // last connection attempt stopped and immediately reconnect. Only the borrower-facing field is
          // read - errorMessage can hold raw vendor or exception text meant for the loan team.
          this.previousAttemptMessage = order.borrowerFacingErrorMessage ?? "";
        }
        this.initializeProvider(order?.integrationProvider ?? this.task.thirdPartyIntegrationProvider);
      },
      error: (err: any) => {
        if (err.status == 404) {
          this.initializeProvider(this.task.thirdPartyIntegrationProvider);
        } else {
          this.errorMessage = 'Unable to load income/employment verification. Please contact your loan officer.';
          this.loading = false;
          this._cdr.detectChanges();
        }
      },
      complete: () => {
      }
    }

    this._spinner.show();
    this._historySubscription = this._portalServiceFactory.createBorrower().getVerificationOrderHistoryForTask(this.task.taskId).subscribe(observer);
    this._historySubscription.add(() => {
      this._spinner.hide();
    });
  }

  private initializeProvider(provider: string | null | undefined): void {
    if (provider !== 'FormFree' && provider !== 'Truv' && provider !== 'Argyle' && provider !== 'PlaidCheck') {
      this.errorMessage = 'Unable to determine the income/employment verification provider. Please contact your loan officer.';
      this.loading = false;
      this._cdr.detectChanges();
      return;
    }

    if (provider === 'PlaidCheck' && !this.plaidCheckEnabled) {
      this.errorMessage = 'The income/employment verification provider is unavailable. Please contact your loan officer.';
      this.loading = false;
      this._cdr.detectChanges();
      return;
    }

    this.verificationProvider = provider === 'PlaidCheck' ? null : provider;
    this.showProviderSelection = this.voeEnabled && this.plaidCheckEnabled && this.verificationProvider !== null;
    this.selectProvider(provider);
  }

  setEnrollmentWidget(): void {
    if (this._enrollmentInitialized || !this.provider || this.provider === 'PlaidCheck') return;
    this._enrollmentInitialized = true;

    this.loading = true;
    this._enrollmentSubscription = this._incomeService.initializeBorrowerIncomeAndEmploymentVerification(this.task.borrowerId, this.applicationId, this.provider).subscribe({
      next: (response: BorrowerVerificationUserEnrollmentResponse) => {
        if (!response.success) {
          this.errorMessage = response.errorMessage || "Unable to initialize income/employment verification. Please contact your loan officer.";
          this.loading = false;
          this._cdr.detectChanges();
          return;
        }
        this.thirdPartyEnrollmentWidget = response.htmlWidgetSrc;
        this.usesOrdersApi = response.usesOrdersApi;
        this.borrowerVerificationOrderId = response.borrowerVerificationOrderId;
        this.loading = false;
        this._cdr.detectChanges();
      },
      error: (error: any) => {
        const serverMessage = error?.error?.message || error?.message || '';
        if (serverMessage.toLowerCase().includes('phone')) {
          this.errorMessage = 'The phone number on your application is missing or invalid. Please go back and enter a valid phone number to continue.';
        } else {
          this.errorMessage = "Failed to initialize verification. Please contact your loan officer.";
        }
        this.loading = false;
        this._cdr.detectChanges();
      }
    });
  }

  ngOnDestroy(): void {
    this._historySubscription?.unsubscribe();
    this._enrollmentSubscription?.unsubscribe();
    this._plaidCheckService.destroyHandler();
  }

  protected selectProvider(provider: VerificationProvider): void {
    this._enrollmentSubscription?.unsubscribe();
    this.provider = provider;
    this.thirdPartyEnrollmentWidget = '';
    this.borrowerVerificationOrderId = null;
    this.usesOrdersApi = false;
    this.errorMessage = '';
    this.loading = false;
    this.plaidCheckConsentGiven = false;
    this._enrollmentInitialized = false;
    if (provider !== 'PlaidCheck') {
      this.setEnrollmentWidget();
    }
    this.updateDialogWidth();
    this._cdr.detectChanges();
  }

  // Argyle is the ONLY provider that gets the narrow-width + increased-height layout;
  // Truv, FormFree, AccountChek and Plaid keep their original layout untouched. The
  // .modal-dialog lives outside this component's template, so we toggle the class on
  // it directly once the provider is known.
  private updateDialogWidth(): void {
    const dialog = (this._elementRef.nativeElement as HTMLElement).closest('.modal-dialog');
    dialog?.classList.toggle('verification-argyle', this.provider === 'Argyle');
  }

  protected async startPlaidCheckVerification(): Promise<void> {
    if (!this.plaidCheckConsentGiven) {
      this._notifyService.showError("Please provide consent to continue with Plaid Check verification.", "Consent Required");
      return;
    }

    this.plaidCheckLoading = true;
    this.errorMessage = "";
    this._cdr.detectChanges();

    try {
      const initResponse = await firstValueFrom(
        this._plaidCheckService.initializePlaidCheckForIncome(this.task.borrowerId, this.applicationId)
      );

      if (!initResponse.success) {
        this.errorMessage = initResponse.errorMessage || "Unable to initialize income/employment verification. Please contact your loan officer.";
        this.plaidCheckLoading = false;
        this._cdr.detectChanges();
        return;
      }

      const linkToken = initResponse.htmlWidgetSrc;
      const result = await this._plaidCheckService.openPlaidLink(linkToken);

      if (result.success) {
        this._notifyService.showSuccess("Income/employment verification initiated successfully.", "Success");
        this.activeModal.close();
      } else if (result.cancelled) {
        console.log('User cancelled Plaid Link');
      } else if (result.error) {
        this.errorMessage = this._plaidCheckService.getErrorMessage(result.error);
        this._cdr.detectChanges();
      }
    } catch (error: any) {
      this.errorMessage = error?.message || "Failed to verify income/employment. Please try again.";
      this._notifyService.showError(this.errorMessage, "Error");
      this._cdr.detectChanges();
    } finally {
      this.plaidCheckLoading = false;
      this._cdr.detectChanges();
    }
  }

  protected onComplete(): void {
    this.activeModal.close();
  }

  protected onClose(): void {
    this.activeModal.close();
  }
}
